import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { bookingsService } from '../../services/api';
import AddItemForm from './otherEdits/AddItemForm';
import UpdateItemForm from './otherEdits/UpdateItemForm';
import ApplyDiscountForm from './otherEdits/ApplyDiscountForm';
import ReallocateForm from './otherEdits/ReallocateForm';

/**
 * OtherEditsTab - Comprehensive tab for dynamic booking management
 * Allows adding, updating, removing, applying discounts, and reallocating rooms, beds, and courses
 */
function OtherEditsTab({ booking, onUpdate, onClose }) {
  // Main state
  const [activeOperation, setActiveOperation] = useState('add'); // add, update, remove, discount, reallocate
  const [activeEntityType, setActiveEntityType] = useState('room'); // room, bed, course
  const [selectedItem, setSelectedItem] = useState(null);
  const [bookingItems, setBookingItems] = useState({ rooms: [], beds: [], courses: [] });
  const [loading, setLoading] = useState(false);

  // Load booking items (rooms, beds, courses)
  useEffect(() => {
    loadBookingItems();
  }, [booking.id]);

  const loadBookingItems = async () => {
    try {
      setLoading(true);
      // Get detailed booking data
      const response = await bookingsService.getById(booking.id);
      const bookingData = response.data || response;

      setBookingItems({
        rooms: bookingData.bookedRooms || [],
        beds: bookingData.bookedBeds || [],
        courses: bookingData.bookedCourses || []
      });
    } catch (error) {
      console.error('Error loading booking items:', error);
      toast.error('Failed to load booking items');
    } finally {
      setLoading(false);
    }
  };

  // Handle successful operation
  const handleOperationSuccess = (response) => {
    toast.success('Operation completed successfully');
    loadBookingItems(); // Reload items
    setSelectedItem(null); // Clear selection
    if (onUpdate) {
      onUpdate(); // Notify parent to refresh
    }
  };

  // Handle remove operation
  const handleRemoveItem = async () => {
    if (!selectedItem) {
      toast.error('Please select an item to remove');
      return;
    }

    const confirmMessage = `Are you sure you want to remove this ${activeEntityType} from the booking? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      if (activeEntityType === 'room') {
        await bookingsService.removeRoomFromBooking(booking.id, selectedItem.id);
        toast.success('Room removed successfully');
      } else if (activeEntityType === 'bed') {
        await bookingsService.removeBedFromBooking(booking.id, selectedItem.id);
        toast.success('Bed removed successfully');
      } else if (activeEntityType === 'course') {
        await bookingsService.removeCourseFromBooking(booking.id, selectedItem.id);
        toast.success('Course removed successfully');
      }

      handleOperationSuccess();
    } catch (error) {
      console.error('Error removing item:', error);
      toast.error(error.error || error.message || `Failed to remove ${activeEntityType}`);
    } finally {
      setLoading(false);
    }
  };

  // Get current items based on entity type
  const getCurrentItems = () => {
    if (activeEntityType === 'room') return bookingItems.rooms;
    if (activeEntityType === 'bed') return bookingItems.beds;
    if (activeEntityType === 'course') return bookingItems.courses;
    return [];
  };

  const operations = [
    { id: 'add', label: 'Add', description: 'Add new item to booking' },
    { id: 'update', label: 'Update', description: 'Update existing item' },
    { id: 'remove', label: 'Remove', description: 'Remove item from booking' },
    { id: 'discount', label: 'Discount', description: 'Apply discount to item' },
    { id: 'reallocate', label: 'Reallocate', description: 'Move to different resource' }
  ];

  const entityTypes = [
    { id: 'room', label: 'Rooms', count: bookingItems.rooms.length },
    { id: 'bed', label: 'Beds', count: bookingItems.beds.length },
    { id: 'course', label: 'Courses', count: bookingItems.courses.length }
  ];

  // Filter operations based on entity type
  const availableOperations = activeEntityType === 'course'
    ? operations.filter(op => op.id !== 'reallocate') // Courses can't be reallocated
    : operations;

  return (
    <div>
      <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600', color: '#24332A' }}>
        Other Edits - Dynamic Booking Management
      </h3>

      <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#5F6B61', lineHeight: '1.6' }}>
        Manage booking resources dynamically. Add, update, remove, apply discounts, or reallocate rooms, beds, and courses 
        without canceling the booking. All changes are tracked and can be audited.
      </p>

      {/* Entity Type Selection */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '8px' }}>
          Select Resource Type:
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
          {entityTypes.map(type => (
            <button
              key={type.id}
              onClick={() => {
                setActiveEntityType(type.id);
                setSelectedItem(null);
                // Reset to add operation if current operation is not available for this entity type
                if (type.id === 'course' && activeOperation === 'reallocate') {
                  setActiveOperation('add');
                }
              }}
              style={{
                padding: '16px',
                border: activeEntityType === type.id ? '2px solid #1A3E2A' : '1px solid #D2CDBF',
                borderRadius: '8px',
                backgroundColor: activeEntityType === type.id ? '#E1E9E2' : 'white',
                color: activeEntityType === type.id ? '#0F2619' : '#36433B',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>{type.label}</span>
              <span style={{ 
                fontSize: '12px',
                padding: '2px 8px',
                borderRadius: '12px',
                backgroundColor: activeEntityType === type.id ? '#1A3E2A' : '#E3DFD3',
                color: activeEntityType === type.id ? 'white' : '#5F6B61'
              }}>
                {type.count} in booking
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Operation Selection */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '8px' }}>
          Select Operation:
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
          {availableOperations.map(op => (
            <button
              key={op.id}
              onClick={() => {
                setActiveOperation(op.id);
                setSelectedItem(null);
              }}
              title={op.description}
              style={{
                padding: '12px',
                border: activeOperation === op.id ? '2px solid #1A3E2A' : '1px solid #D2CDBF',
                borderRadius: '8px',
                backgroundColor: activeOperation === op.id ? '#E1E9E2' : 'white',
                color: activeOperation === op.id ? '#0F2619' : '#36433B',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <span>{op.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Current Items List (for operations that need selection) */}
      {['update', 'remove', 'discount', 'reallocate'].includes(activeOperation) && (
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '8px' }}>
            Select {activeEntityType.charAt(0).toUpperCase() + activeEntityType.slice(1)} from Booking:
          </label>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#F5F3EB', borderRadius: '8px' }}>
              <div style={{ fontSize: '14px', color: '#5F6B61' }}>Loading...</div>
            </div>
          ) : getCurrentItems().length === 0 ? (
            <div style={{ 
              backgroundColor: '#F9EEEB', 
              padding: '20px', 
              borderRadius: '8px', 
              border: '1px solid #E5B3A9',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '14px', color: '#5F241B' }}>
                No {activeEntityType}s in this booking. Please add one first.
              </div>
            </div>
          ) : (
            <div style={{ 
              maxHeight: '200px', 
              overflowY: 'auto', 
              border: '1px solid #D2CDBF', 
              borderRadius: '8px', 
              padding: '8px',
              backgroundColor: 'white'
            }}>
              {getCurrentItems().map((item) => (
                <div
                  key={item.id}
                  onClick={() => setSelectedItem(item)}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    backgroundColor: selectedItem?.id === item.id ? '#E1E9E2' : '#F5F3EB',
                    border: selectedItem?.id === item.id ? '2px solid #1A3E2A' : '1px solid #E3DFD3',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A3E2A', marginBottom: '4px' }}>
                    {activeEntityType === 'room' && `Room ${item.roomNumber || item.room?.roomNumber} - ${item.roomTypeName || item.roomType?.name}`}
                    {activeEntityType === 'bed' && `Bed ${item.bedNumber || item.bed?.bedNumber} in Room ${item.roomNumber || item.room?.roomNumber}`}
                    {activeEntityType === 'course' && `${item.courseName || item.course?.name}`}
                  </div>
                  <div style={{ fontSize: '13px', color: '#5F6B61' }}>
                    {(activeEntityType === 'room' || activeEntityType === 'bed') && (
                      <>
                        {new Date(item.checkIn).toLocaleDateString()} - {new Date(item.checkOut).toLocaleDateString()} 
                        {' '}• {item.nights || 0} nights • ₹{item.finalAmount || 0}
                      </>
                    )}
                    {activeEntityType === 'course' && (
                      <>
                        Qty: {item.quantity || 1} • ₹{item.fee || 0} each • Total: ₹{item.finalAmount || 0}
                      </>
                    )}
                  </div>
                  {item.status && (
                    <div style={{ 
                      display: 'inline-block',
                      marginTop: '4px',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      backgroundColor: item.status === 'CONFIRMED' ? '#E1E9E2' : '#F3E6CB',
                      color: item.status === 'CONFIRMED' ? '#0F2619' : '#684A1F'
                    }}>
                      {item.status}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Operation Forms */}
      <div style={{ marginTop: '24px' }}>
        {activeOperation === 'add' && (
          <AddItemForm
            booking={booking}
            entityType={activeEntityType}
            onSuccess={handleOperationSuccess}
            onCancel={() => setActiveOperation('')}
          />
        )}

        {activeOperation === 'update' && (
          <UpdateItemForm
            booking={booking}
            entityType={activeEntityType}
            selectedItem={selectedItem}
            onSuccess={handleOperationSuccess}
            onCancel={() => setSelectedItem(null)}
          />
        )}

        {activeOperation === 'remove' && selectedItem && (
          <div style={{ backgroundColor: '#F5F3EB', padding: '20px', borderRadius: '8px', border: '1px solid #E3DFD3' }}>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
              Remove {activeEntityType.charAt(0).toUpperCase() + activeEntityType.slice(1)}
            </h4>

            <div style={{ 
              backgroundColor: '#F9EEEB', 
              padding: '16px', 
              borderRadius: '6px', 
              marginBottom: '16px',
              border: '1px solid #E5B3A9'
            }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#5F241B', marginBottom: '8px' }}>
                Warning: This action cannot be undone
              </div>
              <div style={{ fontSize: '13px', color: '#481B14', lineHeight: '1.6' }}>
                You are about to permanently remove this {activeEntityType} from the booking. 
                The booking total will be recalculated and any associated charges will be removed.
              </div>
            </div>

            <div style={{ backgroundColor: '#F3E6CB', padding: '12px', borderRadius: '6px', marginBottom: '16px', border: '1px solid #B68235' }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#684A1F', marginBottom: '4px' }}>
                Item to Remove:
              </div>
              <div style={{ fontSize: '13px', color: '#4F3818' }}>
                {activeEntityType === 'room' && `Room ${selectedItem.roomNumber || selectedItem.room?.roomNumber}`}
                {activeEntityType === 'bed' && `Bed ${selectedItem.bedNumber || selectedItem.bed?.bedNumber}`}
                {activeEntityType === 'course' && `${selectedItem.courseName || selectedItem.course?.name}`}
                <span style={{ marginLeft: '8px', fontWeight: '600' }}>
                  • Amount: ₹{selectedItem.finalAmount}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setSelectedItem(null)}
                disabled={loading}
                style={{
                  backgroundColor: 'white',
                  color: '#36433B',
                  border: '1px solid #D2CDBF',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveItem}
                disabled={loading}
                style={{
                  backgroundColor: loading ? '#A5A89B' : '#8E3628',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Removing...' : 'Confirm Removal'}
              </button>
            </div>
          </div>
        )}

        {activeOperation === 'discount' && (
          <ApplyDiscountForm
            booking={booking}
            entityType={activeEntityType}
            selectedItem={selectedItem}
            onSuccess={handleOperationSuccess}
            onCancel={() => setSelectedItem(null)}
          />
        )}

        {activeOperation === 'reallocate' && activeEntityType !== 'course' && (
          <ReallocateForm
            booking={booking}
            entityType={activeEntityType}
            selectedItem={selectedItem}
            onSuccess={handleOperationSuccess}
            onCancel={() => setSelectedItem(null)}
          />
        )}
      </div>

      {/* Help/Info Section */}
      <div style={{ 
        marginTop: '32px',
        backgroundColor: '#F0F3EE',
        padding: '16px',
        borderRadius: '8px',
        border: '1px solid #1A3E2A'
      }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#0F2619', marginBottom: '8px' }}>
          Quick Tips
        </div>
        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#0B1D13', lineHeight: '1.8' }}>
          <li><strong>Add:</strong> Add new rooms, beds, or courses to expand the booking</li>
          <li><strong>Update:</strong> Modify dates, pricing, or status of existing items</li>
          <li><strong>Remove:</strong> Remove items from booking (requires confirmation)</li>
          <li><strong>Discount:</strong> Apply percentage or fixed discounts with audit trail</li>
          <li><strong>Reallocate:</strong> Move guests to different rooms/beds (availability checked automatically)</li>
        </ul>
        <div style={{ marginTop: '12px', fontSize: '12px', color: '#0F2619', fontStyle: 'italic' }}>
          All changes are saved immediately and booking totals are recalculated automatically.
        </div>
      </div>
    </div>
  );
}

export default OtherEditsTab;
